import React, { useRef, useEffect } from "react";
import { useCanvas } from "../../context/CanvasContext";
import { BackgroundParticlesProps, Particle } from "./types";
import {
  LINE_RENDERING,
  PARTICLE_CONFIG,
  PERFORMANCE,
  GRADIENTS,
  PHYSICS,
} from "./constants";

const BackgroundParticles: React.FC<BackgroundParticlesProps> = ({
  isSlim = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particles = useRef<Particle[]>([]);
  const fpsRef = useRef<number>(60);
  const lastFrameTimeRef = useRef<number>(performance.now());
  const frameCountRef = useRef<number>(0);

  const mousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isMouseActive = useRef<boolean>(false);
  const fadeStartTime = useRef<number>(Date.now());
  const clickPos = useRef<{ x: number; y: number } | null>(null);
  const clickTime = useRef<number>(0);
  const extraParticles = useRef<number>(0);
  const lastClickTimeRef = useRef<number>(0);
  const pendingClicksRef = useRef<number>(0);
  const { isCanvasVisible } = useCanvas();
  let animationFrameId: number;

  const getParticleCount = () => {
  const width = window.innerWidth;
  const cores = navigator.hardwareConcurrency || 4;
  if (cores <= 2) return Math.floor(PARTICLE_CONFIG.MOBILE_COUNT * 0.7);
  if (width <= 480) return PARTICLE_CONFIG.MOBILE_COUNT;
  if (width <= 768) return PARTICLE_CONFIG.TABLET_COUNT;
  return PARTICLE_CONFIG.BASE_COUNT;
};

  const getConnectionDistance = () => {
    return window.innerWidth <= 768
      ? LINE_RENDERING.MOBILE_CONNECTION_DISTANCE
      : LINE_RENDERING.CONNECTION_DISTANCE;
  };

  let resizeTimeout: NodeJS.Timeout;
  const resize = (): void => {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const container = canvas.parentElement;
      if (!container) return;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      initParticles();
    }, 200); // 200ms debounce for less frequent resizing
  };

  const initParticles = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.width;
    const height = canvas.height;
    const particleCount = getParticleCount();

    particles.current = Array.from(
      { length: particleCount },
      (): Particle => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * PARTICLE_CONFIG.BASE_SPEED * 3, // Increased initial velocity
        vy: (Math.random() - 0.5) * PARTICLE_CONFIG.BASE_SPEED * 3,
        ox: Math.random() * width,
        oy: Math.random() * height,
        alpha: 0.3 + Math.random() * 0.4, // Reduced base opacity for softer look
        angle: Math.random() * Math.PI * 2,
        speed: PARTICLE_CONFIG.BASE_SPEED * (1 + Math.random()), // Faster base speed
        noiseOffset: Math.random() * 1000,
        angleSpeed: (Math.random() - 0.5) * PARTICLE_CONFIG.ORBITAL_SPEED, // Faster rotation
        radius: Math.random() * 50 + 20, // Random orbit radius
        centerX: Math.random() * width, // Random orbit center
        centerY: Math.random() * height, // Random orbit center
        lastClickForce: 0,
      })
    );
  };

  const updateParticles = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x: mx, y: my } = mousePos.current;
    const width = canvas.width;
    const height = canvas.height;
    const time = Date.now() * 0.001; // Current time in seconds

    particles.current.forEach((p: Particle) => {
      // Random movement pattern
      p.angle += p.angleSpeed;

      // Circular motion + random drift
      p.centerX += (Math.random() - 0.5) * 0.5;
      p.centerY += (Math.random() - 0.5) * 0.5;

      // Calculate new position
      p.x = p.centerX + Math.cos(p.angle) * p.radius;
      p.y = p.centerY + Math.sin(p.angle) * p.radius;

      // Add some noise-based movement
      p.x += Math.sin(time * p.angleSpeed + p.noiseOffset) * 0.5;
      p.y += Math.cos(time * p.angleSpeed + p.noiseOffset) * 0.5;

      // Wrap around edges for smoother movement
      if (p.centerX < -p.radius) p.centerX = width + p.radius;
      if (p.centerX > width + p.radius) p.centerX = -p.radius;
      if (p.centerY < -p.radius) p.centerY = height + p.radius;
      if (p.centerY > height + p.radius) p.centerY = -p.radius;

      // Keep original mouse interaction
      if (isMouseActive.current) {
        const dx = mx - p.x;
        const dy = my - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < PHYSICS.THICKNESS) {
          const force = -PHYSICS.THICKNESS / (dist * dist);
          p.centerX += dx * force * 0.01;
          p.centerY += dy * force * 0.01;
        }
      }

      // Enhanced mouse interaction
      if (isMouseActive.current) {
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < PHYSICS.MOUSE_RADIUS) {
          const force = (PHYSICS.MOUSE_RADIUS - dist) / PHYSICS.MOUSE_RADIUS;
          const angle = Math.atan2(dy, dx);
          const pushX = Math.cos(angle) * force * PHYSICS.MOUSE_FORCE;
          const pushY = Math.sin(angle) * force * PHYSICS.MOUSE_FORCE;

          // Immediate position adjustment
          p.x += pushX * PHYSICS.REPULSION_SPEED;
          p.y += pushY * PHYSICS.REPULSION_SPEED;

          // Update center position
          p.centerX += pushX * PHYSICS.REPULSION_SPEED * 0.5;
          p.centerY += pushY * PHYSICS.REPULSION_SPEED * 0.5;
        }
      }

      // Add click repulsion effect
      if (clickPos.current) {
        const dx = p.x - clickPos.current.x;
        const dy = p.y - clickPos.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const timeSinceClick = Date.now() - clickTime.current;

        if (dist < PHYSICS.CLICK_RADIUS && timeSinceClick < 1000) {
          const force =
            (PHYSICS.CLICK_FORCE * (1 - timeSinceClick / 1000)) / (dist + 1);
          p.centerX += (dx / dist) * force;
          p.centerY += (dy / dist) * force;
          p.lastClickForce = force;
        } else {
          p.lastClickForce *= 0.95; // Decay the force
        }
      }

      // Apply drag
      p.vx *= PHYSICS.DRAG;
      p.vy *= PHYSICS.DRAG;
      p.speed = Math.min(
        PARTICLE_CONFIG.BASE_SPEED * 2,
        Math.sqrt(p.vx * p.vy + p.vy * p.vy)
      );
    });
  };

  const renderParticles = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, GRADIENTS.START);
    gradient.addColorStop(1, GRADIENTS.END);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Cache connection distances
    const connectionDistance = getConnectionDistance();
    const baseConnectionDistance = LINE_RENDERING.CONNECTION_DISTANCE;

    // Enhanced line rendering
    ctx.save();
    ctx.shadowBlur = 12;
    ctx.shadowColor = `rgba(${LINE_RENDERING.COLOR}, 0.3)`;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const particleArr = particles.current;
    for (let i = 0; i < particleArr.length; i++) {
      const p1 = particleArr[i];
      for (let j = i + 1; j < particleArr.length; j++) {
        const p2 = particleArr[j];
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < connectionDistance) {
          const progress = 1 - dist / baseConnectionDistance;
          const alpha =
            LINE_RENDERING.MIN_ALPHA +
            (LINE_RENDERING.MAX_ALPHA - LINE_RENDERING.MIN_ALPHA) *
              Math.pow(progress, 1.5);
          const width =
            LINE_RENDERING.MIN_WIDTH +
            (LINE_RENDERING.MAX_WIDTH - LINE_RENDERING.MIN_WIDTH) *
              Math.pow(progress, 2);

          ctx.strokeStyle = `rgba(${LINE_RENDERING.COLOR}, ${alpha})`;
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }
    }
    ctx.restore();

    // Draw particles with glow
    ctx.save();
    ctx.shadowBlur = 8;
    particleArr.forEach((p) => {
      const gradient = ctx.createRadialGradient(
        p.x,
        p.y,
        0,
        p.x,
        p.y,
        PARTICLE_CONFIG.SIZE * 2
      );
      gradient.addColorStop(0, `rgba(${LINE_RENDERING.COLOR}, ${p.alpha})`);
      gradient.addColorStop(1, `rgba(${LINE_RENDERING.COLOR}, 0)`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(p.x, p.y, PARTICLE_CONFIG.SIZE, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  };

  const checkPerformance = () => {
    const currentFPS = frameCountRef.current;
    frameCountRef.current = 0;
    fpsRef.current = currentFPS;

    // If FPS drops too low, remove some particles
    if (
      currentFPS < PERFORMANCE.MIN_FPS &&
      particles.current.length > PARTICLE_CONFIG.BASE_COUNT
    ) {
      const removeCount = Math.ceil(
        (particles.current.length - PARTICLE_CONFIG.BASE_COUNT) * 0.2
      );
      particles.current = particles.current.slice(
        0,
        particles.current.length - removeCount
      );
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    const now = Date.now();
    if (
      now - lastClickTimeRef.current < PHYSICS.CLICK_COOLDOWN ||
      pendingClicksRef.current >= PHYSICS.CLICK_BATCH_LIMIT
    ) {
      return;
    }

    lastClickTimeRef.current = now;
    pendingClicksRef.current++;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    clickPos.current = { x: clickX, y: clickY };
    clickTime.current = Date.now();

    const perClickCount =
      window.innerWidth <= 768
        ? PARTICLE_CONFIG.MOBILE_PER_CLICK
        : PARTICLE_CONFIG.PER_CLICK;

    const maxCount =
      window.innerWidth <= 768
        ? PARTICLE_CONFIG.MOBILE_MAX_COUNT
        : PARTICLE_CONFIG.MAX_COUNT;

    // Always add new particles up to max limit
    if (particles.current.length < maxCount) {
      // Add new particles in a circular pattern
      for (let i = 0; i < perClickCount; i++) {
        const angle = (Math.PI * 2 * i) / PARTICLE_CONFIG.PER_CLICK;
        const distance = Math.random() * PHYSICS.REDISTRIBUTION_RADIUS;

        particles.current.push({
          x: clickX + Math.cos(angle) * distance,
          y: clickY + Math.sin(angle) * distance,
          vx: (Math.random() - 0.5) * PARTICLE_CONFIG.BASE_SPEED * 3,
          vy: (Math.random() - 0.5) * PARTICLE_CONFIG.BASE_SPEED * 3,
          ox: clickX,
          oy: clickY,
          alpha: 0.3 + Math.random() * 0.4,
          angle: angle,
          speed: PARTICLE_CONFIG.BASE_SPEED * (1 + Math.random()),
          noiseOffset: Math.random() * 1000,
          angleSpeed: (Math.random() - 0.5) * PARTICLE_CONFIG.ORBITAL_SPEED * 2,
          radius: Math.random() * 30 + 10,
          centerX: clickX + Math.cos(angle) * distance,
          centerY: clickY + Math.sin(angle) * distance,
          lastClickForce: PHYSICS.CLICK_FORCE,
        });
      }
    }

    // Also redistribute some existing particles
    const particlesToRedistribute = particles.current
      .sort(() => Math.random() - 0.5)
      .slice(0, PHYSICS.REDISTRIBUTION_COUNT);

    particlesToRedistribute.forEach((p) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * PHYSICS.REDISTRIBUTION_RADIUS;
      p.centerX = clickX + Math.cos(angle) * distance;
      p.centerY = clickY + Math.sin(angle) * distance;
      p.lastClickForce = PHYSICS.CLICK_FORCE;
    });

    setTimeout(() => {
      clickPos.current = null;
    }, 1000);

    // Reset pending clicks counter after animation
    setTimeout(() => {
      pendingClicksRef.current = Math.max(0, pendingClicksRef.current - 1);
    }, 1000);
  };

  const animate = (): void => {
    if (!isCanvasVisible) return;

    frameCountRef.current++;

    // Check performance every second
    const now = performance.now();
    if (now - lastFrameTimeRef.current >= PERFORMANCE.CHECK_INTERVAL) {
      checkPerformance();
      lastFrameTimeRef.current = now;
    }

    updateParticles();
    renderParticles();
    animationFrameId = requestAnimationFrame(animate);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    mousePos.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    isMouseActive.current = true;
  };

  const handleMouseOut = (): void => {
    isMouseActive.current = false;
  };

  useEffect(() => {
    if (isCanvasVisible) {
      resize();
      animate();
    }

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isCanvasVisible]);

  return (
    <div
      style={{
        width: "100%",
        height: isSlim ? "80px" : "60vh",
        overflow: "hidden",
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: "var(--primary-bg)",
        margin: 0,
        padding: 0,
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseOut={handleMouseOut}
        onClick={handleClick}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      />
    </div>
  );
};
export default BackgroundParticles;
